import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

interface InvestmentTotalSummaryProps {
  totalMonthly: number;
  basePrice: number;
  isGestao: boolean;
  isInstitucional: boolean;
  clientCount: number;
  clientCapacityCost: number;
  nucleusCount: number;
  nucleusCapacityCost: number;
  extraUsers: number;
  extraUsersCost: number;
  subtotal: number;
  minPricePerClient: number;
  minimumsSubtotal: number;
  volumeDiscount: number;
  totalClientsPrice: number;
  preFloorTotal: number;
  formatCurrency: (value: number) => string;
}

/**
 * Box verde de "Investimento mensal total", com o detalhamento (mensalidade fixa + custo do
 * pacote/condomínios + usuários extras + descontos). Extraído de UnifiedPricingCalculator.tsx (F7).
 */
export const InvestmentTotalSummary: React.FC<InvestmentTotalSummaryProps> = ({
  totalMonthly,
  basePrice,
  isGestao,
  isInstitucional,
  clientCount,
  clientCapacityCost,
  nucleusCount,
  nucleusCapacityCost,
  extraUsers,
  extraUsersCost,
  subtotal,
  minPricePerClient,
  minimumsSubtotal,
  volumeDiscount,
  totalClientsPrice,
  preFloorTotal,
  formatCurrency,
}) => {
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          p: 3,
          bgcolor: '#2e7d32', // Verde escuro para melhor contraste
          borderRadius: 2,
          color: 'white',
          mb: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          Investimento mensal total
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>
          {formatCurrency(totalMonthly)}
        </Typography>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 0.5, fontWeight: 'bold' }}>
            ✓ Mensalidade fixa: {formatCurrency(basePrice)}
          </Typography>
          {isGestao ? (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 0.5 }}>
                Pacote de condomínios ({clientCount}): {formatCurrency(clientCapacityCost)}
              </Typography>
              {extraUsers > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mt: 0.5 }}>
                  Usuários extras ({extraUsers}): {formatCurrency(extraUsersCost)}
                </Typography>
              )}
            </>
          ) : isInstitucional ? (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 0.5 }}>
                Pacote de núcleos ({nucleusCount}): {formatCurrency(nucleusCapacityCost)}
              </Typography>
              {extraUsers > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mt: 0.5 }}>
                  Usuários extras ({extraUsers}): {formatCurrency(extraUsersCost)}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 0.5 }}>
                Subtotal condomínios: {formatCurrency(subtotal)}
              </Typography>
              {minPricePerClient > 0 && minimumsSubtotal > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 0.5 }}>
                  Parcela mínima: {formatCurrency(minimumsSubtotal)}
                </Typography>
              )}
              {volumeDiscount > 0 && (
                <Typography variant="caption" sx={{ color: '#a5d6a7', display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                  Desconto progressivo: -{formatCurrency(volumeDiscount)}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block' }}>
                Total condomínios: {formatCurrency(totalClientsPrice)}
              </Typography>
              {extraUsers > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mt: 0.5 }}>
                  Usuários extras ({extraUsers}): {formatCurrency(extraUsersCost)}
                </Typography>
              )}
            </>
          )}
          {!isGestao && basePrice > 0 && preFloorTotal < basePrice && (
            <Typography variant="caption" sx={{ color: '#ffe082', display: 'block', mt: 0.5 }}>
              Piso mínimo aplicado (basePrice): {formatCurrency(basePrice)}
            </Typography>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default InvestmentTotalSummary;
